// Updated ScanOptions.tsx - Remove the Grid container with upload options
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Upload as UploadIcon,
  AutoAwesome as AutoIcon,
  Phone as PhoneIcon,
  Scanner as ScannerIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledToggleButton = styled(ToggleButton)(() => ({
  borderRadius: '16px !important',
  padding: '16px 24px',
  margin: '0 8px',
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    },
  },
}));

interface ScanOptionsProps {
  options: {
    useCamera: boolean;
    useUpload: boolean;
    useScanner: boolean;
    autoProcess: boolean;
    extractMobile: boolean;
    scannerSettings?: {
      device?: string;
      resolution?: number;
      colorMode?: 'Color' | 'Gray' | 'Lineart';
      pageSize?: 'A4' | 'Letter' | 'Legal';
      source?: 'ADF' | 'Flatbed';
      duplex?: boolean;
    };
  };
  onChange: (key: string, value: any) => void;
  onScan?: () => void;
  isScanning?: boolean;
}

export const ScanOptions: React.FC<ScanOptionsProps> = ({ 
  options, 
  onChange, 
  onScan,
  isScanning 
}) => {
  const [showScannerSettings, setShowScannerSettings] = useState(false);
  const [availableScanners, setAvailableScanners] = useState<any[]>([]);

 useEffect(() => {
  if (options.useScanner) {
    console.log('Fetching scanners from: http://localhost:3000/api/scanners');
    
    fetch('http://localhost:3000/api/scanners')
      .then(res => {
        console.log('Response status:', res.status);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Raw scanners data received:', data);
        
        // Handle different response structures
        let scannersArray = [];
        
        if (Array.isArray(data)) {
          // If API returns array directly
          scannersArray = data;
        } else if (data && typeof data === 'object') {
          // If API returns { data: [...] } or { scanners: [...] }
          if (Array.isArray(data.data)) {
            scannersArray = data.data;
          } else if (Array.isArray(data.scanners)) {
            scannersArray = data.scanners;
          } else {
            // If it's a single scanner object, wrap in array
            scannersArray = [data];
          }
        }
        
        console.log('Processed scanners array:', scannersArray);
        setAvailableScanners(scannersArray);
      })
      .catch(err => {
        console.error('Failed to fetch scanners:', err);
        setAvailableScanners([]); // Always set to empty array on error
      });
  }
}, [options.useScanner]);

  const handleScanMode = (_event: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (newMode) {
      onChange('useCamera', newMode === 'camera');
      onChange('useUpload', newMode === 'upload');
      onChange('useScanner', newMode === 'scanner');
    }
  };

  return (
    <>
      <Paper
  sx={{
    p: 2,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '24px',
    border: '1px solid rgba(255,215,0,0.2)',
    mb: 2,
  }}
>
  <Typography variant="h6" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
    <AutoIcon sx={{ color: '#FFD700' }} />
    Scan Options
    <Chip
      label="AI Powered"
      size="small"
      sx={{ ml: 1, background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}
    />
  </Typography>

  {/* Toggle Buttons - Stacked vertically */}
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
  <ToggleButtonGroup
    value={
      options.useCamera ? 'camera' :
      options.useUpload ? 'upload' :
      options.useScanner ? 'scanner' : null
    }
    exclusive
    onChange={handleScanMode}
    aria-label="scan mode"
    orientation="vertical"
    sx={{
      width: '100%',
      '& .MuiToggleButton-root': {
        borderRadius: '12px !important',
        margin: '4px 0',
        padding: '12px',
        borderColor: '#1976d2',
        color: '#1976d2',

        '&:hover': {
          borderColor: '#1565c0',
          backgroundColor: '#e3f2fd',
        },

        '&.Mui-selected': {
          backgroundColor: '#1976d2',
          color: '#fff',
          borderColor: '#1976d2',

          '&:hover': {
            backgroundColor: '#1565c0',
          }
        }
      }
    }}
  >
      <StyledToggleButton value="camera">
        <CameraIcon sx={{ mr: 1 }} />
        Camera
      </StyledToggleButton>
      <StyledToggleButton value="upload">
        <UploadIcon sx={{ mr: 1 }} />
        Upload
      </StyledToggleButton>
      <StyledToggleButton value="scanner">
        <ScannerIcon sx={{ mr: 1 }} />
        Scanner
      </StyledToggleButton>
    </ToggleButtonGroup>
  </Box>

  {/* Toggle Buttons - Single Row */}
{/* <Box sx={{ display: 'flex', mb: 3 }}>
  <ToggleButtonGroup
    value={
      options.useCamera ? 'camera' :
      options.useUpload ? 'upload' :
      options.useScanner ? 'scanner' : null
    }
    exclusive
    onChange={handleScanMode}
    aria-label="scan mode"
    sx={{
      width: '100%',
      display: 'flex',
      '& .MuiToggleButton-root': {
        flex: 1,              // makes 3 buttons equal width
        borderRadius: '12px !important',
        margin: '0 4px',
        padding: '12px',
        borderColor: '#1976d2',
        color: '#1976d2',

        '&:hover': {
          borderColor: '#1565c0',
          backgroundColor: '#e3f2fd',
        },

        '&.Mui-selected': {
          backgroundColor: '#1976d2',
          color: '#fff',
          borderColor: '#1976d2',

          '&:hover': {
            backgroundColor: '#1565c0',
          }
        }
      }
    }}
  >
    <StyledToggleButton value="camera">
      <CameraIcon sx={{ mr: 1 }} />
      Camera
    </StyledToggleButton>

    <StyledToggleButton value="upload">
      <UploadIcon sx={{ mr: 1 }} />
      Upload
    </StyledToggleButton>

    <StyledToggleButton value="scanner">
      <ScannerIcon sx={{ mr: 1 }} />
      Scanner
    </StyledToggleButton>
  </ToggleButtonGroup>
</Box> */}

  {/* Scanner Settings Section */}
  {options.useScanner && (
    <Box sx={{ mb: 1, p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ScannerIcon sx={{ fontSize: 18, color: '#FFD700' }} />
          Scanner Settings
        </Typography>
        <Button
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => setShowScannerSettings(true)}
          sx={{ 
            color: '#FFD700',
            '&:hover': {
              backgroundColor: 'rgba(255,215,0,0.1)',
            }
          }}
        >
          Configure
        </Button>
      </Box>
      
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1 }}>
        {options.scannerSettings?.device || 'Default Scanner'} • 
        {options.scannerSettings?.resolution || 300}dpi • 
        {options.scannerSettings?.source || 'ADF'}
      </Typography>
      

{onScan && (
  <Button
    fullWidth
    variant="contained"
    onClick={onScan}
    disabled={isScanning}
    sx={{
      mt: 1,
      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
      color: '#1a1a2e',
      fontWeight: 600,
      py: 1.5,
      '&:hover': {
        background: 'linear-gradient(135deg, #FFA500, #FFD700)',
      },
      '&:disabled': {
        background: 'rgba(255,215,0,0.3)',
      }
    }}
  >
    {isScanning ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress sx={{ height: 4, color: '#1a1a2e' }} />
        <Typography>Scanning...</Typography>
      </Box>
    ) : (
      'Start Scan'
    )}
  </Button>
)}
    </Box>
  )}

  <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

  {/* Switches */}
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
    <FormControlLabel
      control={
        <Switch
          checked={options.autoProcess}
          onChange={(e) => onChange('autoProcess', e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#FFD700',
              '&:hover': {
                backgroundColor: 'rgba(255,215,0,0.1)',
              },
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#FFD700',
            },
          }}
        />
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
          <AutoIcon sx={{ fontSize: 18, color: '#FFD700' }} />
          <Typography sx={{ color: 'white' }}>Auto Process</Typography>
        </Box>
      }
      sx={{ m: 0 }}
    />
    
    <FormControlLabel
      control={
        <Switch
          checked={options.extractMobile}
          onChange={(e) => onChange('extractMobile', e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#FFD700',
              '&:hover': {
                backgroundColor: 'rgba(255,215,0,0.1)',
              },
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#FFD700',
            },
          }}
        />
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
          <PhoneIcon sx={{ fontSize: 18, color: '#FFD700' }} />
          <Typography sx={{ color: 'white' }}>Auto Extract Mobile</Typography>
        </Box>
      }
      sx={{ m: 0 }}
    />
  </Box>

  {/* Scanner Settings Dialog */}
  <Dialog
  open={showScannerSettings}
  onClose={() => setShowScannerSettings(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: '24px',
      background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    },
  }}
>
  <DialogTitle sx={{ color: 'white' }}>Scanner Settings</DialogTitle>
  <DialogContent>
    <Box sx={{ mt: 2 }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Scanner Device</InputLabel>
        <Select
          value={options.scannerSettings?.device || ''}
          label="Scanner Device"
          onChange={(e) => onChange('scannerSettings', {
            ...options.scannerSettings,
            device: e.target.value
          })}
          sx={{
            color: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,0.2)',
            },
          }}
        >
          <MenuItem value="">Default Scanner</MenuItem>
          {Array.isArray(availableScanners) && availableScanners.length > 0 ? (
            availableScanners.map((scanner) => (
              <MenuItem key={scanner.id || scanner.deviceId || Math.random()} value={scanner.id || scanner.deviceId}>
                {scanner.name || scanner.displayName || 'Unknown Scanner'}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No scanners found</MenuItem>
          )}
        </Select>
      </FormControl>

        <Typography gutterBottom sx={{ color: 'white' }}>Resolution (DPI)</Typography>
        <Slider
          value={options.scannerSettings?.resolution || 300}
          onChange={(_e, val) => onChange('scannerSettings', {
            ...options.scannerSettings,
            resolution: val
          })}
          step={50}
          marks
          min={150}
          max={600}
          valueLabelDisplay="auto"
          sx={{ mb: 2, color: '#FFD700' }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Color Mode</InputLabel>
          <Select
            value={options.scannerSettings?.colorMode || 'Color'}
            label="Color Mode"
            onChange={(e) => onChange('scannerSettings', {
              ...options.scannerSettings,
              colorMode: e.target.value
            })}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <MenuItem value="Color">Color</MenuItem>
            <MenuItem value="Gray">Grayscale</MenuItem>
            <MenuItem value="Lineart">Black & White</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Page Size</InputLabel>
          <Select
            value={options.scannerSettings?.pageSize || 'A4'}
            label="Page Size"
            onChange={(e) => onChange('scannerSettings', {
              ...options.scannerSettings,
              pageSize: e.target.value
            })}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <MenuItem value="A4">A4</MenuItem>
            <MenuItem value="Letter">Letter</MenuItem>
            <MenuItem value="Legal">Legal</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Source</InputLabel>
          <Select
            value={options.scannerSettings?.source || 'ADF'}
            label="Source"
            onChange={(e) => onChange('scannerSettings', {
              ...options.scannerSettings,
              source: e.target.value
            })}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <MenuItem value="ADF">Auto Document Feeder</MenuItem>
            <MenuItem value="Flatbed">Flatbed</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={options.scannerSettings?.duplex || false}
              onChange={(e) => onChange('scannerSettings', {
                ...options.scannerSettings,
                duplex: e.target.checked
              })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#FFD700',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#FFD700',
                },
              }}
            />
          }
          label={
            <Typography sx={{ color: 'white' }}>Duplex (Double-sided)</Typography>
          }
        />
      </Box>
    </DialogContent>
    <DialogActions sx={{ p: 3 }}>
      <Button 
        onClick={() => setShowScannerSettings(false)} 
        sx={{ 
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          }
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={() => setShowScannerSettings(false)}
        sx={{
          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
          color: '#1a1a2e',
          fontWeight: 600,
          '&:hover': {
            background: 'linear-gradient(135deg, #FFA500, #FFD700)',
          },
        }}
      >
        Save Settings
      </Button>
    </DialogActions>
  </Dialog>
</Paper>
    </>
  );
};

// // src/components/ScanOptions.tsx
// import React from 'react';
// import {
//   Box,
//   Paper,
//   Typography,
//   ToggleButton,
//   ToggleButtonGroup,
//   FormControlLabel,
//   Switch,
//   Divider,
//   Chip,
// } from '@mui/material';
// import {
//   CameraAlt as CameraIcon,
//   Upload as UploadIcon,
//   AutoAwesome as AutoIcon,
//   Phone as PhoneIcon,
// } from '@mui/icons-material';
// import { styled } from '@mui/material/styles';

// const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
//   borderRadius: '16px !important',
//   padding: '16px 24px',
//   margin: '0 8px',
//   '&.Mui-selected': {
//     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//     color: 'white',
//     '&:hover': {
//       background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
//     },
//   },
// }));

// export interface ScanOptionsProps {
//   options: {
//     useCamera: boolean;
//     useUpload: boolean;
//     autoProcess: boolean;
//     extractMobile: boolean;
//   };
//   onChange: (key: string, value: any) => void;
// }

// export const ScanOptions: React.FC<ScanOptionsProps> = ({ options, onChange }) => {
//   const handleScanMode = (event: React.MouseEvent<HTMLElement>, newMode: string | null) => {
//     if (newMode) {
//       onChange('useCamera', newMode === 'camera');
//       onChange('useUpload', newMode === 'upload');
//     }
//   };

//   return (
//     <Paper
//       sx={{
//         p: 3,
//         background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
//         borderRadius: '24px',
//         border: '1px solid rgba(255,215,0,0.2)',
//         mb: 3,
//       }}
//     >
//       <Typography variant="h6" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
//         <AutoIcon sx={{ color: '#FFD700' }} />
//         Scan Options
//         <Chip
//           label="AI Powered"
//           size="small"
//           sx={{ ml: 1, background: 'linear-gradient(135deg, #FFD700, #FFA500)' }}
//         />
//       </Typography>

//       <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
//         <ToggleButtonGroup
//           value={options.useCamera ? 'camera' : options.useUpload ? 'upload' : null}
//           exclusive
//           onChange={handleScanMode}
//           aria-label="scan mode"
//         >
//           <StyledToggleButton value="camera">
//             <CameraIcon sx={{ mr: 1 }} />
//             Camera
//           </StyledToggleButton>
//           <StyledToggleButton value="upload">
//             <UploadIcon sx={{ mr: 1 }} />
//             Upload
//           </StyledToggleButton>
//         </ToggleButtonGroup>
//       </Box>

//       <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

//       <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
//         <FormControlLabel
//           control={
//             <Switch
//               checked={options.autoProcess}
//               onChange={(e) => onChange('autoProcess', e.target.checked)}
//               sx={{
//                 '& .MuiSwitch-switchBase.Mui-checked': {
//                   color: '#FFD700',
//                   '&:hover': {
//                     backgroundColor: 'rgba(255,215,0,0.1)',
//                   },
//                 },
//                 '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
//                   backgroundColor: '#FFD700',
//                 },
//               }}
//             />
//           }
//           label={
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//               <AutoIcon sx={{ fontSize: 20, color: '#FFD700' }} />
//               <Typography sx={{ color: 'white' }}>Auto Process</Typography>
//             </Box>
//           }
//         />
//         <FormControlLabel
//           control={
//             <Switch
//               checked={options.extractMobile}
//               onChange={(e) => onChange('extractMobile', e.target.checked)}
//               sx={{
//                 '& .MuiSwitch-switchBase.Mui-checked': {
//                   color: '#FFD700',
//                   '&:hover': {
//                     backgroundColor: 'rgba(255,215,0,0.1)',
//                   },
//                 },
//                 '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
//                   backgroundColor: '#FFD700',
//                 },
//               }}
//             />
//           }
//           label={
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//               <PhoneIcon sx={{ fontSize: 20, color: '#FFD700' }} />
//               <Typography sx={{ color: 'white' }}>Auto Extract Mobile</Typography>
//             </Box>
//           }
//         />
//       </Box>
//     </Paper>
//   );
// };
